import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast, Toaster } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupMode, setSetupMode] = useState(false);

  useEffect(() => {
    // Check if admin is already logged in
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      navigate("/admin-panel");
    }
  }, [navigate]);

  const handleSetupMaster = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/setup-master`);
      toast.success(response.data.message);
      setSetupMode(false);
    } catch (error) {
      if (error.response?.data?.detail?.includes("already exists")) {
        toast.info("Admin already exists. Please login.");
        setSetupMode(false);
      } else {
        toast.error(error.response?.data?.detail || "Setup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/login`, {
        username,
        password
      });
      
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("adminUser", JSON.stringify(response.data.admin));
      
      toast.success("Login successful!");
      navigate("/admin-panel");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative" data-testid="admin-login-page">
      <Toaster position="top-center" richColors />
      
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>
      
      <Card className="w-full max-w-md shadow-xl relative z-10">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-full object-cover" />
          </div>
          <CardTitle className="text-2xl">Admin Panel</CardTitle>
          <CardDescription>
            LUNAWAT FOUNDATION Administration
          </CardDescription>
        </CardHeader>

        <CardContent>
          {setupMode ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Click below to create the master administrator account with default credentials:
              </p>
              <div className="p-4 bg-muted rounded-lg text-sm">
                <p><strong>Username:</strong> master</p>
                <p><strong>Password:</strong> master@123</p>
              </div>
              <Button
                onClick={handleSetupMaster}
                disabled={loading}
                className="w-full"
                data-testid="setup-master-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Master Admin"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSetupMode(false)}
                className="w-full"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Username</label>
                <Input
                  type="text"
                  placeholder="Enter admin username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="admin-username-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                data-testid="admin-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Login"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setSetupMode(true)}
                  className="text-sm text-primary hover:underline"
                  data-testid="setup-link"
                >
                  First time? Setup Master Admin
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
